import { Configuration, PopupRequest } from "@azure/msal-browser";

// Validate required environment variables
const validateConfig = () => {
  const clientId = process.env.REACT_APP_AZURE_CLIENT_ID;
  const tenantId = process.env.REACT_APP_AZURE_TENANT_ID;
  
  if (!clientId || clientId === "your-client-id-here") {
    console.error("❌ REACT_APP_AZURE_CLIENT_ID is not configured. Please set up Azure AD app registration.");
    console.error("📖 See AZURE_AD_SETUP.md for instructions.");
  }
  
  return { clientId, tenantId };
};

const { clientId, tenantId } = validateConfig();

// MSAL configuration — authenticate against the MBS Group Dev tenant where the
// app is registered and admin consent is already granted.  Guest users from
// other KPMG domains (e.g. kpmg.co.uk) can sign in as long as they have guest
// access in this tenant.
export const msalConfig: Configuration = {
  auth: {
    clientId: clientId || "your-client-id-here",
    authority: `https://login.microsoftonline.com/${tenantId || "organizations"}`,
    redirectUri: process.env.REACT_APP_REDIRECT_URI || "http://localhost:3002",
    postLogoutRedirectUri: process.env.REACT_APP_POST_LOGOUT_REDIRECT_URI || "http://localhost:3002",
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: true,
  },
};

// Delegated scope exposed by this app registration ("Expose an API"). Access
// tokens for this scope are issued to *our* API, so the backend can verify their
// signature, audience and tenant (Microsoft Graph tokens cannot be verified by
// third parties). The app is pre-authorized for this scope, so no consent prompt.
export const apiScope =
  process.env.REACT_APP_API_SCOPE || `api://${clientId}/access_as_user`;

export const loginRequest: PopupRequest = {
  scopes: [apiScope, "openid", "profile", "email"],
};

// Graph API scopes (optional - for getting user info)
export const graphConfig = {
  graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
};

// Function to validate if user has enterprise account
export const isEnterpriseAccount = (account: any): boolean => {
  if (!account) return false;
  
  // Check if email has a company domain (not personal accounts)
  const email = account.username || account.idTokenClaims?.preferred_username;
  if (!email) return false;
  
  // Exclude personal Microsoft accounts
  const personalDomains = [
    'outlook.com', 'hotmail.com', 'live.com', 'msn.com', 
    'gmail.com', 'yahoo.com', 'aol.com'
  ];
  
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain || personalDomains.includes(domain)) {
    return false;
  }
  
  // Additional check for tenant type if available
  const tenantType = account.idTokenClaims?.tid_typ;
  if (tenantType && tenantType === 'PerAzure') {
    return false; // Personal Azure tenant
  }
  
  return true; // Assume enterprise if domain is not personal
};

// Organization data extraction
export const extractOrganizationData = (account: any) => {
  const email = account.username || account.idTokenClaims?.preferred_username;
  const domain = email ? email.split('@')[1] : '';
  const organizationName = account.idTokenClaims?.aud_friendly_name || 
                          account.idTokenClaims?.org || 
                          domain.split('.')[0]; // Fallback to domain name
  
  return {
    organizationId: account.homeAccountId,
    organizationName: organizationName,
    organizationDomain: domain,
    userEmail: email,
    userId: account.localAccountId,
    userName: account.idTokenClaims?.name || account.name,
    tenantId: account.tenantId
  };
};
